import { Controller } from "@hotwired/stimulus";
import { getReportGenerator } from "../packs/reports/reports";
import { filteredItemsStore } from "../stores/items/filtered_items_store";
import { regionStore } from "../stores/locales/region_store";
import { buildingStore } from "../stores/locales/building_store";
import { areaStore } from "../stores/locales/area_store";
import { roomStore } from "../stores/locales/room_store";

// Connects to data-controller="report-selection"
export default class extends Controller {
  static values = { url: String }

  connect() {
    this._hideFilters(); // Hide filters section initially

    // Whether the CURRENT filteredItemsStore value came from a real user action
    // (clicking Apply or Clear Filters) rather than a programmatic reset (e.g.
    // selectReport clearing the store when switching reports). See
    // onFilteredDevicesUpdate for why this can't be inferred from array length.
    this._userTriggeredFilterUpdate = false;

    // Subscribe to filteredItemsStore to react when filters are applied
    this.unsubscribe = filteredItemsStore.subscribe((devices) => {
      this.onFilteredDevicesUpdate(devices);
    });

    // Listen directly on the Apply / Clear Filters buttons (owned by
    // filters--item-filters, in shared/_filters.html.erb) so we know the NEXT
    // filteredItemsStore update reflects a real user action, regardless of
    // whether it turns out to contain zero or more devices.
    this._markUserTriggered = () => { this._userTriggeredFilterUpdate = true };
    this._applyButtonEl = document.getElementById('applyFiltersButton');
    this._clearFiltersButtonEl = document.getElementById('clearFiltersButton');
    this._applyButtonEl?.addEventListener('click', this._markUserTriggered);
    this._clearFiltersButtonEl?.addEventListener('click', this._markUserTriggered);
  }

  disconnect() {
    // Clean up subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this._applyButtonEl?.removeEventListener('click', this._markUserTriggered);
    this._clearFiltersButtonEl?.removeEventListener('click', this._markUserTriggered);
  }

  // User selects a report
  selectReport(event) {
    // Show standard reports, hide custom reports
    const standardSection = document.getElementById('standard-reports-section');
    const customSection = document.getElementById('custom-reports-section');

    if (standardSection) standardSection.style.display = 'block';
    if (customSection) customSection.style.display = 'none';

    // Highlight the selected report
    const selectedButton = event.target.closest('label');
    const reportButtons = document.querySelectorAll('.report-button');

    // Remove active class from all buttons
    reportButtons.forEach(button => {
      button.parentElement.classList.remove('active');
    });

    // Add active class to the selected button
    selectedButton.classList.add('active');

    // Store the selected report type
    this.selectedReportType = event.target.value;

    // Clear the previous filters and results
    this._clearFiltersAndResults();

    // Show and expand the filter section
    this._showAndExpandFilters();

    // Hide the graph by default
    this._hideGraph();

    // Clear all filter stores to prevent old filter values from being applied
    // when user clicks Apply on the new report
    regionStore.setValue(null);
    buildingStore.setValue(null);
    areaStore.setValue(null);
    roomStore.setValue(null);

    // Clear filteredItemsStore (user must click Apply to generate report). This is a
    // PROGRAMMATIC reset, not a user action -- do not mark it as user-triggered, so
    // onFilteredDevicesUpdate correctly ignores it rather than rendering an empty report.
    this._userTriggeredFilterUpdate = false;
    filteredItemsStore.setValue([]);
  }

  // React to filteredItemsStore updates
  onFilteredDevicesUpdate(devices) {
    // Render whenever a report type is selected AND this update came from a real
    // user action (Apply or Clear Filters) -- NOT from array length. A filter
    // combination can correctly match ZERO devices (e.g. selecting a Project with
    // no devices in it after a different filter previously matched some) -- that
    // empty result must still be rendered, not silently ignored while the
    // PREVIOUS (stale) results stay on screen. Only the store's own programmatic
    // reset on report switch (selectReport) should be ignored, and that path
    // explicitly clears _userTriggeredFilterUpdate itself.
    if (this.selectedReportType && devices && this._userTriggeredFilterUpdate) {
      this._displayReportFromStore(this.selectedReportType, devices);
    }
  }

  // Clear filters and results
  _clearFiltersAndResults() {
    // Clear all filter select elements
    document.querySelectorAll('.filters select').forEach(select => {
        // If the tom-select hasn't been instantiated yet, so it returns undefined, we can
        // reasonably expect that the select doesn't have items.
        select.tomselect?.clear();
    });

    // Clear the report results
    const tableEl = document.getElementById('table-holder');
    if (tableEl) {
      // Always clear the table controller data if it exists
      if (window.Stimulus) {
        const argus = window.Stimulus.getControllerForElementAndIdentifier(tableEl, 'argus--table');
        if (argus) {
          argus.clearData();
        }
      }
      // Always clear the HTML to ensure old data is removed
      tableEl.innerHTML = '';
    }
    document.getElementById('chart-title').innerHTML = '';
  }

  // Clear filters and results
  clearFilters(event) {
    event.preventDefault();

    // Clear all filter select elements
    document.querySelectorAll('.filters select').forEach(select => {
        select.tomselect?.clear();
    });

    // Clear the report results
    this._clearTable();
    this._clearGraph();
    
    // Clear filteredItemsStore
    filteredItemsStore.setValue([]);
  }
  
  _clearTable() {
    const tableElement = document.getElementById('table-holder');
    if (tableElement) {
      if (window.Stimulus) {
        const argusTable = window.Stimulus.getControllerForElementAndIdentifier(tableElement, 'argus--table')
        if (argusTable) {
          argusTable.clearData()
        }
      }
      // Always clear the HTML to ensure old data is removed
      tableElement.innerHTML = '';
    }
  }
  
  _clearGraph() {
    const graphHolder = document.getElementById('graph-holder');
    const chartTitleElement = document.getElementById('chart-title');
    
    if (graphHolder) {
      graphHolder.style.display = 'none';
    }
    
    if (chartTitleElement) {
      chartTitleElement.innerHTML = '';
    }
  }

  // Toggle graph visibility
  toggleGraph(event) {
    const graphHolder = document.getElementById('graph-holder');
    if (graphHolder.style.display === 'none' || graphHolder.style.display === '') {
        graphHolder.style.display = 'block';
    } else {
        this._hideGraph();
    }
  }

  // Hide graph
  _hideGraph() {
      const graphHolder = document.getElementById('graph-holder');
      if (graphHolder) {
        graphHolder.style.display = 'none';
      }
  }

  _showAndExpandFilters() {
    const filterSection = document.getElementById('filter-section');
    filterSection.style.display = 'block'; // Show the filter section

    const filterCollapse = document.getElementById('filtersCollapse');
    if (filterCollapse && filterCollapse.classList.contains('collapse')) {
      filterCollapse.classList.add('show'); // Expand the filter section
    }
  }

  // Display report from store data
  _displayReportFromStore(reportTitle, devices) {
    const reportController = getReportGenerator(reportTitle);
    
    // Pass devices from store
    reportController.displayReportFromStore(devices);
  }

  _showFilters() {
    document.getElementById('filter-section').style.display = 'block'; // Show the filter section
  }

  _hideFilters() {
    document.getElementById('filter-section').style.display = 'none'; // Hide the filter section
  }
}